import { DatePipe } from '@angular/common';

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const languageToDateFormatsMap = new Map<string, string>( [
    ['de', 'dd.mm.yy'],
    ['en', 'mm/dd/yy']
] );

export const languageToMonthYearFormatsMap = new Map<string, string>( [
    ['de', 'mm.yy'],
    ['en', 'mm/yy']
] );

/**
 * get dateFormat by language locale
 */
export function getDateFormatByLanguageLocale( lang: string ): string {
    return languageToDateFormatsMap.get( lang );
}

/**
 * formats a Date object as string to english fromat - 'yyyy-mm-dd'
 */
export function formatEnglishDateFromString( date: Date ): string {
    if ( !date ) {
        return null;
    }
    let day = date.getDate();
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    let dayStr = day > 9 ? day : '0' + day;
    let monthStr = month > 9 ? month : '0' + month;
    return year + '-' + monthStr + '-' + dayStr;
}

/**
 * formats a Date object as string to german fromat - 'dd.mm.yyyy'
 */
export function formatGermanDateFromString( date: Date ): string {
    if ( !date ) {
        return null;
    }
    let day = date.getDate();
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    let dayStr = day > 9 ? day : '0' + day;
    let monthStr = month > 9 ? month : '0' + month;
    return dayStr + '.' + monthStr + '.' + year;
}

/**
 * Convert the given input to a pure date value, where hours and below are truncated,
 * in the timezone given by the browser locale.
 *
 * @param value
 * @return date in browser locale
 */
export function convertToLocalDate( value: string | number | Date ): Date {
    let dateStr = convertToIsoDateString( value ); // truncates hours and below
    if (dateStr === null || dateStr === undefined) {
        return null;
    }
    let intermediateDate = new Date( dateStr );
    // new Date ( dateStr ) creates the given Date in UTC time.
    // To get the correct date for the timezone given by the browser locale we must add the "timezone offset".
    return new Date( intermediateDate.getTime() + intermediateDate.getTimezoneOffset() * 60000 );
}

/**
 * Convert the given input to a pure date string in ISO format, where hours and below are truncated.
 *
 * @param value
 * @return date string in ISO format
 */
export function convertToIsoDateString( value: string | number | Date ): string {
    try {
        if ( value instanceof Date || typeof(value) === 'number' ) {
            return new DatePipe( 'en-US' ).transform( value, 'yyyy-MM-dd' );
        }
        if ( typeof(value) === 'string' ) {
            return parseAndConvertToIsoDateString(value);
        }
    } catch ( error ) {
        console.log( 'Error parsing date string', value, ':', error );
        return null;
    }
    console.log( 'Could not parse date', value, 'of type', typeof(value) );
    return null;
}

/**
 * Convert a date string into a date string in ISO format.
 *
 * Permitted input formats are ISO format yyyy-mm-dd,  german format dd.mm.yyyy and american english format mm/dd/yyyy,
 * optionally followed by a time component which will be truncated.
 *
 * @param value the date string to parse
 * @return the date string in IOS format, or null if the input could not be parsed
 */
function parseAndConvertToIsoDateString(value: string): string {
    // strip time component if present
    if (value.indexOf(' ') > 0) {
        // date&time format like yyyy-mm-dd hh:mm:ss
        value = value.split(' ')[0];
    }
    if (value.indexOf('T') > 0) {
        // date&time format like yyyy-mm-ddThh:mm:ss
        value = value.split('T')[0];
    }

    // read day, month and year
    let dd: string;
    let mm: string;
    let yyyy: string;
    if ( value.indexOf( '-' ) >= 0 ) {
        // the value is in ISO format
        let p1: number = value.indexOf( '-' );
        yyyy = value.substring( 0, p1 );
        let p2: number = value.indexOf( '-', p1 + 1 );
        mm = value.substring( p1 + 1, p2 );
        dd = value.substring( p2 + 1 );
    } else if ( value.indexOf( '/' ) >= 0 ) {
        // the value is in american english date format
        let p1: number = value.indexOf( '/' );
        mm = value.substring( 0, p1 );
        let p2: number = value.indexOf( '/', p1 + 1 );
        dd = value.substring( p1 + 1, p2 );
        yyyy = value.substring( p2 + 1 );
    } else if ( value.indexOf( '.' ) >= 0 ) {
        // the value is in german date format
        let p1: number = value.indexOf( '.' );
        dd = value.substring( 0, p1 );
        let p2: number = value.indexOf( '.', p1 + 1 );
        mm = value.substring( p1 + 1, p2 );
        yyyy = value.substring( p2 + 1 );
    }
    if (!dd || !mm || !yyyy) {
        return null;
    }

    // validate year
    if (!isInteger(yyyy)) {
        return null;
    }
    let yyyyi = parseInt(yyyy, 10);
    if (yyyyi<1000 || yyyyi>9999) { // ensure 4 digits year
        console.log( 'Date string containing invalid year value:', value );
        return null;
    }
    let isLeapYear = yyyyi % 4 === 0 || (yyyyi % 4 === 0 && yyyyi % 100 !== 0);

    // validate month
    if (!isInteger(mm)) {
        return null;
    }
    let mmi = parseInt(mm, 10);
    if (mmi<=0 || mmi>12) {
        console.log( 'Date string containing invalid month value:', value );
        return null;
    }
    let daysOfMonth = DAYS_PER_MONTH[mmi-1];
    if (mmi === 2 && isLeapYear) {
        daysOfMonth = 29;
    }

    // validate day
    if (!isInteger(dd)) {
        return null;
    }
    let ddi = parseInt(dd, 10);
    if (ddi<=0 || ddi>daysOfMonth) { // abhängig von Monat, Schaltjahre
        console.log( 'Date string containing invalid day value:', value );
        return null;
    }

    // return date in ISO format
    return yyyy + '-' + mm + '-' + dd;
}

function isInteger(s: string): boolean {
    for (let c of s) {
        if (c < '0' || c > '9') {
            return false;
        }
    }
    return true;
}